/**
 * The open folder's right-click menu, wired: what each entry sends to the mail
 * server, how the list redraws after it, and the two dialogs it can raise.
 *
 * Every entry is one request against the account's real mailbox, so the list
 * only changes once the server has said yes - with one exception. A read or
 * flag mark is drawn straight away and put back if the server refuses, because
 * a bold line that stays bold for a second after "Mark as read" looks like a
 * click that missed. A move or a delete takes the row out only when it has
 * happened: a message the list drops and the server still holds is a message
 * the operator believes is gone.
 *
 * After each change the rail hears what happened to its counts (see
 * `folderCounts.ts`); the list never re-reads the whole folder for one row.
 */
import { useState } from "react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import type { ImapMessageRow } from "../../../../lib/mail";
import { deleteImapMessage, moveImapMessage, setImapMessageFlags } from "../../../../lib/mailActions";
import { useToast } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { moveCounts, removedCounts, seenCounts, type FolderCountChange } from "./folderCounts";
import { imapFolderError } from "./imapFolderErrors";
import { ImapMessageItems, deleteIsPermanent } from "./ImapMessageItems";
import { announceMailbox } from "./mailboxEvents";
import { MoveImapMessageModal } from "./MoveImapMessageModal";
import { sourceKey, type ImapBrowse, type ImapSource } from "./useImapBrowse";

/** The last segment of a folder path, which is what a toast should name. */
const leaf = (path: string) => path.split(/[/.]/).pop() || path;

export function useImapMessageActions(browse: ImapBrowse, source: ImapSource | null) {
  // The server path, for the requests and the menu; the key, for the list.
  const path = source?.path ?? null;
  const mailbox = source?.mailbox;
  const key = source ? sourceKey(source) : "";
  const t = useT();
  const toast = useToast();
  const [moving, setMoving] = useState<ImapMessageRow | null>(null);
  const [purging, setPurging] = useState<ImapMessageRow | null>(null);

  const ref = (folder: string, row: ImapMessageRow) => ({
    path: folder,
    uid: row.uid,
    uidValidity: browse.uidValidity,
    mailbox,
  });
  const announceCounts = (changes: FolderCountChange[]) =>
    announceMailbox({ kind: "counts", mailbox: mailbox ?? "shared", changes });
  const fail = (err: unknown) => toast(imapFolderError(t, err, "email.imap.msg.failed"));

  async function flags(row: ImapMessageRow, change: { seen?: boolean; flagged?: boolean }) {
    if (!path) return;
    const folder = path;
    const before = { seen: row.seen, flagged: row.flagged };
    browse.patchRow(key, row.uid, change);
    try {
      await setImapMessageFlags(ref(folder, row), change);
      if (change.seen !== undefined) {
        announceCounts(seenCounts(folder, before.seen, change.seen));
      }
    } catch (err) {
      browse.patchRow(key, row.uid, before);
      fail(err);
    }
  }

  /** Throws on failure as well as toasting, so the move dialog stays open. */
  async function moveTo(row: ImapMessageRow, to: string) {
    if (!path) return;
    const folder = path;
    try {
      const res = await moveImapMessage(ref(folder, row), to);
      browse.dropRow(key, row.uid);
      announceCounts(moveCounts(res.from, res.to, res.seen));
      toast(t("email.imap.msg.moved", { name: leaf(res.to) }));
    } catch (err) {
      fail(err);
      throw err;
    }
  }

  async function remove(row: ImapMessageRow, permanent: boolean) {
    if (!path) return;
    const folder = path;
    try {
      const res = await deleteImapMessage(ref(folder, row), permanent);
      browse.dropRow(key, row.uid);
      if ("trashed" in res) {
        announceCounts(moveCounts(folder, res.to, res.seen));
        toast(t("email.imap.msg.trashed"));
      } else {
        announceCounts(removedCounts(folder, res.seen));
        toast(t("email.imap.msg.deleted"));
      }
    } catch (err) {
      // The server found there is nowhere to put it - Trash was removed since
      // the list was read. That is a question for the operator, not an error.
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      if (!permanent && body?.error === "needs_permanent") {
        setPurging(row);
        return;
      }
      fail(err);
    }
  }

  /** A delete that can only be permanent asks first; one that goes to Trash does not. */
  function askDelete(row: ImapMessageRow) {
    if (!path) return;
    if (deleteIsPermanent(path, browse.targets)) setPurging(row);
    else void remove(row, false);
  }

  const renderMenu = (row: ImapMessageRow, close: () => void) =>
    path ? (
      <ImapMessageItems
        row={row}
        path={path}
        targets={browse.targets}
        close={close}
        onOpen={browse.open}
        onFlags={(r, change) => void flags(r, change)}
        onMove={(r, to) => void moveTo(r, to).catch(() => undefined)}
        onPickFolder={setMoving}
        onDelete={askDelete}
      />
    ) : null;

  const modals = (
    <>
      {moving && path ? (
        <MoveImapMessageModal
          mailbox={mailbox ?? "shared"}
          path={path}
          row={moving}
          onClose={() => setMoving(null)}
          onMove={(to) => moveTo(moving, to)}
        />
      ) : null}

      {purging ? (
        <ConfirmModal
          title={t("email.imap.msg.deleteTitle")}
          body={t("email.imap.msg.deleteBody")}
          confirmLabel={t("email.imap.msg.deleteForever")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await remove(purging, true);
            setPurging(null);
          }}
          onClose={() => setPurging(null)}
        />
      ) : null}
    </>
  );

  return { renderMenu, modals };
}
